/**
 * Centralized role-based navigation configuration
 * Single source of truth for role-based redirects
 */

import { UserRole } from '../types/auth';

/**
 * Maps user roles to their default dashboard/home routes
 */
const ROLE_ROUTES: Record<UserRole, string> = {
  student: '/dashboard',
  parent: '/parent/dashboard',
  teacher: '/teacher/dashboard',
  admin: '/admin',
  super_admin: '/admin',
};

/**
 * Get the default route for a given user role
 * @param role - The user's role
 * @returns The default route path for that role
 */
export const getDefaultRouteForRole = (role: UserRole): string => {
  return ROLE_ROUTES[role] || '/dashboard';
};

/**
 * Get the default route for a user object
 * @param user - User object with role property
 * @returns The default route path for the user's role
 */
export const getDefaultRouteForUser = (user: { role: UserRole }): string => {
  return getDefaultRouteForRole(user.role);
};

/**
 * Where the sales demo sends each persona.
 *
 * Currently identical to the normal routes, and deliberately so: the demo shows v1 throughout.
 *
 * The student UI exists in two generations — the complete v1 pages, and the in-progress rewrite
 * under /student/v2. v2 covers only the dashboard, quizzes, achievements and problems, so a demo
 * routed there has to fall back to v1 for the leaderboard, sessions, enrolment, teams and profile,
 * changing chrome halfway through. It also is not what production logs students into, so it would
 * be demonstrating something a new customer does not actually get. The animated quiz play and
 * confetti results exist in v1 too, so little is lost.
 *
 * Kept as a seam rather than inlined: if the v2 rewrite is finished, this is the one place to
 * repoint the demo.
 */
const DEMO_ROLE_ROUTES: Partial<Record<UserRole, string>> = {};

export const getDemoRouteForRole = (role: UserRole): string => {
  return DEMO_ROLE_ROUTES[role] || getDefaultRouteForRole(role);
};
